import { useState, useEffect, useRef } from "react";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  getEmailTemplates,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  cloneEmailTemplateToLibrary,
} from "../api/EmailTemplatesApi";
import { getMedia } from "../api/MediaApi";
import { getDepartments } from "../api/HumanRiskScorecardApi";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { apiClient, fastApiClient } from "../App";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Plus, Search, Mail, MessageSquare, Phone, Trash2, Edit, Eye, Copy, Code, Image as ImageIcon, X, Paperclip, Sparkles, Loader2, Check, ChevronsUpDown, Library
} from "lucide-react";
import { toast } from "sonner";

const SIMULATION_ICONS = {
  email: Mail,
  sms: MessageSquare,
  whatsapp: Phone,
};

const SIMULATION_COLORS = {
  email: "bg-[#8c3add]/10 text-[#8c3add]",
  sms: "bg-[#1fa7a1]/10 text-[#1fa7a1]",
  whatsapp: "bg-[#22c55e]/10 text-[#22c55e]",
};

const emptyForm = {
  name: "",
  subject: "",
  body: "",
  html_body: "",
  category: "",
  difficulty: "medium",
  simulation_type: "email",
  sender_id: "",
  has_report_button: false,
  embed_qr: false,
  include_tracking_pixel: true,
  include_tracking_link: true,
};
const SEARCH_KEY = "search";

// Backend ke response se error message nikalta hai (string / FastAPI 422 array / object / network error)
const getErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === "string") return detail;

  // FastAPI 422: [{ loc: [...], msg: "..." }]
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        return field ? `${field}: ${d.msg}` : d.msg || JSON.stringify(d);
      })
      .join(", ");
  }

  if (detail) return JSON.stringify(detail);
  return err.message || fallback; // response hi nahi aaya (network error etc.)
};

const EmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const hasLoadedOnce = useRef(false);
  const [search, setSearch] = useState("");
  const isFirstRender = useRef(true);
  const [filterType, setFilterType] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  // Media-library pickers (replaces local file upload)
  const [mediaImages, setMediaImages] = useState([]);
  const [mediaImagesLoading, setMediaImagesLoading] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const [mediaAttachments, setMediaAttachments] = useState([]);
  const [mediaAttachmentsLoading, setMediaAttachmentsLoading] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);

  // Inline AI generation
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiChannel, setAiChannel] = useState("email");
  const [aiBusy, setAiBusy] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);

  useEffect(() => {
    fetchTemplates(filterType);
  }, [filterType]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = setTimeout(() => fetchTemplates(filterType, search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchDepartments();
  }, []);

  // Load Media library lists whenever the dialog is open, editing an existing
  // email template (CID images / attachments only apply to saved email templates).
  useEffect(() => {
    if (isDialogOpen && editingId && formData.simulation_type === "email") {
      fetchMediaImages();
      fetchMediaAttachments();
    }
  }, [isDialogOpen, editingId, formData.simulation_type]);

  const fetchMediaImages = async () => {
    setMediaImagesLoading(true);
    try {
      const res = await getMedia("image");
      const raw = res.data?.data ?? [];
      setMediaImages(Array.isArray(raw) ? raw : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load images from Media"));
    }
    setMediaImagesLoading(false);
  };

  const fetchMediaAttachments = async () => {
    setMediaAttachmentsLoading(true);
    try {
      const res = await getMedia("attachment");
      const raw = res.data?.data ?? [];
      setMediaAttachments(Array.isArray(raw) ? raw : []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load attachments from Media"));
    }
    setMediaAttachmentsLoading(false);
  };

  const fetchDepartments = async () => {
    setDepartmentsLoading(true);

    try {
      const response = await getDepartments();

      setDepartments(
        Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || []
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load departments"));
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const [cloningId, setCloningId] = useState(null);

  const handleCloneToLibrary = async (templateId) => {
    setCloningId(templateId);
    try {
      await cloneEmailTemplateToLibrary(templateId);
      toast.success("Template cloned to library");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to clone template"));
    } finally {
      setCloningId(null);
    }
  };

  const fetchTemplates = async (type = filterType, q = search) => {
    // First page load par full page loader
    // Filter/search ke time sirf list loader
    if (!hasLoadedOnce.current) {
      setLoading(true);
    } else {
      setListLoading(true);
    }

    try {
      const params = {
        ...(type && type !== "all" ? { simulation_type: type } : {}),
        ...(q ? { [SEARCH_KEY]: q } : {}),
      };

      const response = await getEmailTemplates(params);

      setTemplates(
        Array.isArray(response?.data)
          ? response.data
          : response?.data?.results || []
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load templates"));
    } finally {
      hasLoadedOnce.current = true;
      setLoading(false);
      setListLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setSelectedImageIds([]);
    setSelectedAttachmentIds([]);
    setIsDialogOpen(true);
  };

  const openEdit = (template) => {
    setEditingId(template.id);
    setFormData({
      name: template.name || "",
      subject: template.subject || "",
      body: template.body || "",
      html_body: template.html_body || "",
      category: template.category || "",
      difficulty: template.difficulty || "medium",
      simulation_type: template.simulation_type || "email",
      sender_id: template.sender_id || "",
      has_report_button: !!template.has_report_button,
      embed_qr: !!template.embed_qr,
      include_tracking_pixel: template.include_tracking_pixel !== false,
      include_tracking_link: template.include_tracking_link !== false,
    });
    // Pre-fill from the template's already-attached media so both the
    // "currently attached" list and the picker start in sync. Removing an
    // item just drops its id from these arrays — no API call until Save.
    const existingMedia = template.media_data || [];
    setSelectedImageIds(existingMedia.filter((m) => m.media_type === "image").map((m) => m.id));
    setSelectedAttachmentIds(existingMedia.filter((m) => m.media_type === "attachment").map((m) => m.id));
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };
    // SMS / WhatsApp don't need subject / html_body
    if (payload.simulation_type !== "email") {
      payload.html_body = "";
    }

    // Selected Media images/attachments go straight into the same payload —
    // backend attaches them itself when saving the template.
    if (payload.simulation_type === "email") {
      payload.media_ids = [...selectedImageIds, ...selectedAttachmentIds];
    }

    try {
      if (editingId) {
        await updateEmailTemplate(editingId, payload);
      } else {
        await createEmailTemplate(payload);
      }

      toast.success(editingId ? "Template updated" : "Template created");
      setIsDialogOpen(false);
      setFormData(emptyForm);
      setEditingId(null);
      setSelectedImageIds([]);
      setSelectedAttachmentIds([]);
      fetchTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save template"));
    }
  };

  // Template being targeted for delete (dialog me message dikhane ke liye)
  const templateToDelete = deleteTemplateId
    ? templates.find((t) => t.id === deleteTemplateId)
    : null;

  const handleDelete = async () => {
    if (!deleteTemplateId) return;

    try {
      await deleteEmailTemplate(deleteTemplateId);
      toast.success("Template deleted");
      setDeleteTemplateId(null);
      fetchTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to delete template"));
    }
  };

  // --- Media-library selection (CID images) ---
  const toggleImageSelection = (id) => {
    setSelectedImageIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // --- Media-library selection (Attachments) ---
  const toggleAttachmentSelection = (id) => {
    setSelectedAttachmentIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  // Frontend check hata diya: empty prompt par bhi API chalegi, error backend se aayega
  const handleInlineAIGenerate = async () => {
    setAiBusy(true);
    try {
      const res = await fastApiClient.post("/ai/generate-template", {
        scenario: aiPrompt,
        target_role: "employee",
        tone: "urgent",
        channels: [aiChannel],
        save: true,
        model: "claude-sonnet-4-6",
      });
      const savedCount = res.data.saved_template_ids?.length || 0;
      if (savedCount > 0) {
        toast.success(`AI created ${savedCount} template${savedCount === 1 ? "" : "s"}. Refreshing…`);
        setAiPrompt("");
        fetchTemplates();
      } else {
        toast.error("AI didn't return usable content — try a different prompt");
      }
    } catch (err) {
      toast.error(getErrorMessage(err, "AI generation failed"));
    }
    setAiBusy(false);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy": return "bg-green-100 text-green-700";
      case "medium": return "bg-amber-100 text-amber-700";
      case "hard": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  // Search ab backend se hota hai, isliye client-side filter nahi
  const filteredTemplates = Array.isArray(templates) ? templates : [];

  // Backend returns attached media in a single `media_data` array (mixed
  // images + attachments), distinguished by `media_type`. Only show items
  // still present in the local selection state — removing one just drops
  // its id from selectedImageIds/selectedAttachmentIds (see toggle functions),
  // no API call happens until Save.
  const editingTemplate = editingId ? templates.find((t) => t.id === editingId) : null;
  const mediaDataList = editingTemplate?.media_data || [];
  const cidImages = mediaDataList.filter(
    (m) => m.media_type === "image" && selectedImageIds.includes(m.id)
  );
  const attachments = mediaDataList.filter(
    (m) => m.media_type === "attachment" && selectedAttachmentIds.includes(m.id)
  );

  if (loading) {
    return <CulsightPageLoader variant="page" />;
  }

  const simType = formData.simulation_type;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6 animate-fadeIn" data-testid="email-templates-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Simulation Templates</h1>
            <p className="text-gray-500 text-sm mt-1">Email, SMS &amp; WhatsApp phishing templates</p>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={openCreate}
                className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2"
                data-testid="create-template-btn"
              >
                <Plus size={18} />
                New Template
              </Button>
            </TooltipTrigger>
          </Tooltip>
        </div>

        {/* Inline AI Template Generator */}
        <Card className="border border-[#8c3add]/30 shadow-sm bg-gradient-to-r from-[#8c3add]/5 to-[#1fa7a1]/5" data-testid="inline-ai-generator">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[#8c3add]/15 mt-1">
                <Sparkles size={18} className="text-[#8c3add]" />
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-[#1f1f23]">Generate a template with AI</p>
                  <Badge className="bg-[#8c3add]/10 text-[#8c3add] text-[10px]">Claude Sonnet 4.6</Badge>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="e.g. Amazon refund scam targeting Finance managers, with logo, hero image and urgent CTA"
                    className="flex-1 bg-white"
                    data-testid="inline-ai-prompt"
                    onKeyDown={(e) => { if (e.key === "Enter" && !aiBusy) { e.preventDefault(); handleInlineAIGenerate(); } }}
                  />
                  <Select value={aiChannel} onValueChange={setAiChannel}>
                    <SelectTrigger className="sm:w-40 bg-white" data-testid="inline-ai-channel"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      {/* <SelectItem value="whatsapp">WhatsApp</SelectItem> */}
                    </SelectContent>
                  </Select>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleInlineAIGenerate}
                        disabled={aiBusy}
                        className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2"
                        data-testid="inline-ai-generate-btn"
                      >
                        {aiBusy ? <CulsightPageLoader variant="inline" /> : <Sparkles size={14} />}
                        {aiBusy ? "Generating…" : "Generate & Save"}
                      </Button>
                    </TooltipTrigger>
                  </Tooltip>
                </div>
                <p className="text-[11px] text-gray-500">
                  AI produces rich, branded HTML with images, buttons and inline CSS — saved directly to your library.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search templates..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="templates-search"
            />
          </div>
          <Tabs value={filterType} onValueChange={setFilterType}>
            <TabsList>
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="email" data-testid="filter-email">
                <Mail size={14} className="mr-1" /> Email
              </TabsTrigger>
              <TabsTrigger value="sms" data-testid="filter-sms">
                <MessageSquare size={14} className="mr-1" /> SMS
              </TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="filter-whatsapp">
                <Phone size={14} className="mr-1" /> WhatsApp
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Templates Grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch relative min-h-[200px]"
          data-testid="templates-grid"
        >
          {listLoading && <CulsightPageLoader variant="overlay" />}
          {filteredTemplates.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              No templates found
            </div>
          ) : (
            filteredTemplates.map((template) => {
              const type = template.simulation_type || "email";
              const Icon = SIMULATION_ICONS[type] || Mail;
              const isDeleteable = template.deleteable !== false;
              return (
                <Card
                  key={template.id}
                  className="h-full border border-gray-100 shadow-sm card-hover"
                  data-testid={`template-card-${template.id}`}
                >
                  <CardContent className="p-5 h-full flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div className={`p-2 rounded-lg ${SIMULATION_COLORS[type] || SIMULATION_COLORS.email}`}>
                        <Icon size={20} />
                      </div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        <Badge className={SIMULATION_COLORS[type] || SIMULATION_COLORS.email}>{type.toUpperCase()}</Badge>
                        <Badge className={getDifficultyColor(template.difficulty)}>{template.difficulty}</Badge>
                      </div>
                    </div>

                    <h3 className="font-semibold text-[#1f1f23] mt-3 line-clamp-1">{template.name}</h3>
                    {type === "email" && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-1">Subject: {template.subject}</p>
                    )}
                    <p className="text-sm text-gray-400 mt-2 line-clamp-2">{template.body}</p>

                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-xs text-gray-400">
                          {new Date(template.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex gap-1">
                          {type !== "whatsapp" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 hover:bg-[#1fa7a1]/10"
                                  onClick={() => handleCloneToLibrary(template.id)}
                                  disabled={cloningId === template.id}
                                  data-testid={`clone-template-${template.id}`}
                                >
                                  {cloningId === template.id ? (
                                    <CulsightPageLoader variant="inline" />
                                  ) : (
                                    <Library size={16} className="text-[#1fa7a1]" />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Clone to Template Library</TooltipContent>
                            </Tooltip>
                          )}

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 hover:bg-[#8c3add]/10"
                                onClick={() => setViewTemplate(template)}
                                data-testid={`view-template-${template.id}`}
                              >
                                <Eye size={16} className="text-[#8c3add]" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Preview</TooltipContent>
                          </Tooltip>

                          {type !== "whatsapp" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 hover:bg-[#1fa7a1]/10"
                                  onClick={() => openEdit(template)}
                                  data-testid={`edit-template-${template.id}`}
                                >
                                  <Edit size={16} className="text-[#1fa7a1]" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Edit</TooltipContent>
                            </Tooltip>
                          )}

                          {type !== "whatsapp" && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 hover:bg-[#8c3add]/10"
                                  onClick={() => {
                                    navigator.clipboard.writeText(template.html_body || template.body);
                                    toast.success("Copied to clipboard");
                                  }}
                                >
                                  <Copy size={16} className="text-gray-500" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Copy Content</TooltipContent>
                            </Tooltip>
                          )}

                          {/* Delete — disabled with an explanatory tooltip when
                              the backend says this template isn't deleteable
                              (e.g. still referenced/in use somewhere). */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={!isDeleteable ? "cursor-not-allowed" : undefined}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={`p-2 ${isDeleteable ? "hover:bg-red-50" : "opacity-40 pointer-events-none"}`}
                                  onClick={() => isDeleteable && setDeleteTemplateId(template.id)}
                                  disabled={!isDeleteable}
                                  data-testid={`delete-template-${template.id}`}
                                >
                                  {isDeleteable ? (
                                    <Trash2 size={16} className="text-red-500" />
                                  ) : (
                                    <span className="relative inline-flex items-center justify-center">
                                      <Trash2 size={16} className="text-red-500" />
                                      <span className="absolute w-[18px] h-[2px] bg-red-500 rotate-45 rounded-full" />
                                    </span>
                                  )}
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {isDeleteable
                                ? "Delete"
                                : "You can't delete this template — it's currently in use"}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create / Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) { setEditingId(null); setFormData(emptyForm); setSelectedImageIds([]); setSelectedAttachmentIds([]); } }}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingId ? <Edit size={18} className="text-[#8c3add]" /> : <Plus size={18} className="text-[#8c3add]" />}
                {editingId ? "Edit Template" : "Create Template"}
              </DialogTitle>
            </DialogHeader>
            {/* noValidate: browser ki validation band, validation backend karega */}
            <form onSubmit={handleSubmit} noValidate className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Password Reset Template"
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Simulation Type</Label>
                  <Select
                    value={formData.simulation_type}
                    onValueChange={(value) => setFormData({ ...formData, simulation_type: value })}
                  >
                    <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      {/* <SelectItem value="whatsapp">WhatsApp</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={categoryOpen}
                        className="w-full justify-between font-normal"
                        data-testid="template-category-trigger"
                      >
                        {formData.category || "Select Category"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                      <Command>
                        <CommandInput placeholder="Search category..." data-testid="template-category-search" />
                        <CommandList>
                          {departmentsLoading ? (
                            <div className="flex justify-center py-6">
                              <CulsightPageLoader variant="inline" />
                            </div>
                          ) : departments.length === 0 ? (
                            <div className="py-6 text-center text-sm text-gray-500">No departments found</div>
                          ) : (
                            <>
                              <CommandEmpty>No matching category</CommandEmpty>
                              <CommandGroup>
                                {departments.map((department) => (
                                  <CommandItem
                                    key={department.id}
                                    value={department.name}
                                    onSelect={(currentValue) => {
                                      setFormData({ ...formData, category: currentValue });
                                      setCategoryOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${formData.category === department.name ? "opacity-100" : "opacity-0"
                                        }`}
                                    />
                                    {department.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select
                    value={formData.difficulty}
                    onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {simType === "email" && (
                <div className="space-y-2">
                  <Label>Subject Line</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Urgent: Action Required"
                    data-testid="template-subject-input"
                  />
                </div>
              )}

              {(simType === "sms" || simType === "whatsapp") && (
                <div className="space-y-2">
                  <Label>{simType === "sms" ? "Sender ID (max 11 chars)" : "Sender Name"}</Label>
                  <Input
                    value={formData.sender_id}
                    onChange={(e) => setFormData({ ...formData, sender_id: e.target.value })}
                    placeholder={simType === "sms" ? "HRDEPT" : "HR Department"}
                    maxLength={simType === "sms" ? 11 : 50}
                    data-testid="template-sender-input"
                  />
                </div>
              )}

              {simType === "email" ? (
                <Tabs defaultValue="html" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="html" data-testid="html-tab">
                      <Code size={14} className="mr-1" /> HTML Body
                    </TabsTrigger>
                    <TabsTrigger value="plain" data-testid="plain-tab">Plain Text (fallback)</TabsTrigger>
                  </TabsList>
                  <TabsContent value="html" className="space-y-2">
                    <Label>HTML Content</Label>
                    <Textarea
                      value={formData.html_body}
                      onChange={(e) => setFormData({ ...formData, html_body: e.target.value })}
                      placeholder={'<html><body><img src="cid:logo" /> ... </body></html>'}
                      rows={12}
                      className="font-mono text-xs"
                      data-testid="template-html-input"
                    />
                    <p className="text-xs text-gray-500">
                      Reference CID images as <code className="bg-gray-100 px-1 rounded">{'<img src="cid:logo">'}</code>
                    </p>
                  </TabsContent>
                  <TabsContent value="plain" className="space-y-2">
                    <Label>Plain Text</Label>
                    <Textarea
                      value={formData.body}
                      onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                      placeholder="Plain text version of the email..."
                      rows={8}
                      data-testid="template-body-input"
                    />
                  </TabsContent>
                </Tabs>
              ) : (
                <div className="space-y-2">
                  <Label>{simType === "sms" ? "SMS Message (max 160 chars)" : "WhatsApp Message"}</Label>
                  <Textarea
                    value={formData.body}
                    onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                    placeholder={simType === "sms" ? "Your verification code is..." : "Hello! Your account requires..."}
                    rows={6}
                    maxLength={simType === "sms" ? 160 : 1000}
                    data-testid="template-body-input"
                  />
                  <p className="text-xs text-gray-500">{formData.body.length} characters</p>
                </div>
              )}

              {/* Tracking */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Report Button</Label>
                  <Switch
                    checked={formData.has_report_button}
                    onCheckedChange={(v) => setFormData({ ...formData, has_report_button: v })}
                  />
                </div>
                {simType === "email" && (
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">Tracking Pixel</Label>
                    <Switch
                      checked={formData.include_tracking_pixel}
                      onCheckedChange={(v) => setFormData({ ...formData, include_tracking_pixel: v })}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Tracking Link</Label>
                  <Switch
                    checked={formData.include_tracking_link}
                    onCheckedChange={(v) => setFormData({ ...formData, include_tracking_link: v })}
                  />
                </div>
                {simType === "email" && (
                  <div className="flex items-center justify-between gap-2" data-testid="qr-toggle-wrap">
                    <Label className="text-sm">Embed QR Code</Label>
                    <Switch
                      checked={formData.embed_qr}
                      onCheckedChange={(v) => setFormData({ ...formData, embed_qr: v })}
                      data-testid="qr-toggle"
                    />
                  </div>
                )}
              </div>
              {simType === "email" && formData.embed_qr && (
                <div className="p-3 bg-[#8c3add]/5 border border-[#8c3add]/20 rounded-lg text-xs text-gray-700">
                  💡 QR embedding is <strong>on</strong>. At send time, we replace the placeholder{" "}
                  <code className="bg-white px-1 rounded">{"{{qr_image}}"}</code>{" "}
                  in your HTML with a per-recipient tracked QR code. Insert{" "}
                  <code className="bg-white px-1 rounded">{'<img src="{{qr_image}}" width="180"/>'}</code>{" "}
                  anywhere in the HTML.
                </div>
              )}

              {/* CID Images — now selected from Media library instead of local upload */}
              {simType === "email" && editingId && (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-3" data-testid="cid-images-section">
                  <div className="flex items-center gap-2">
                    <ImageIcon size={16} className="text-[#8c3add]" />
                    <Label className="font-medium">Embedded CID Images</Label>
                  </div>

                  {/* Currently attached CID images */}
                  {cidImages.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {cidImages.map((img) => (
                        <div key={img.id} className="border rounded-lg p-2 flex items-center gap-2 bg-white">
                          <img
                            src={img.url}
                            alt={img.filename}
                            className="w-10 h-10 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-mono truncate">{img.filename}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {img.size ? `${(img.size / 1024).toFixed(1)} KB` : ""}
                            </p>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="p-1 h-auto hover:bg-red-50"
                                onClick={() => toggleImageSelection(img.id)}
                              >
                                <X size={14} className="text-red-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove image</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pick images from Media library — list only opens on "Select" click */}
                  <div className="flex items-center gap-2">
                    <Popover open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          data-testid="select-images-btn"
                        >
                          <ImageIcon size={14} />
                          Select Images{selectedImageIds.length ? ` (${selectedImageIds.length})` : ""}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <p className="text-xs font-medium text-gray-500 mb-2">Images from Media</p>
                        {mediaImagesLoading ? (
                          <div className="flex justify-center py-6">
                            <CulsightPageLoader variant="inline" />
                          </div>
                        ) : mediaImages.length === 0 ? (
                          <div className="py-4 text-center text-sm text-gray-400">
                            No images in Media library yet — upload some in the Media page first.
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                            {mediaImages.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center gap-2 border rounded-lg p-2 cursor-pointer transition-colors ${selectedImageIds.includes(m.id)
                                  ? "border-[#8c3add] bg-[#8c3add]/5"
                                  : "border-gray-200 bg-white hover:bg-gray-50"
                                  }`}
                              >
                                <Checkbox
                                  checked={selectedImageIds.includes(m.id)}
                                  onCheckedChange={() => toggleImageSelection(m.id)}
                                />
                                {m.url ? (
                                  <img src={m.url} alt={m.filename} className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                ) : (
                                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <ImageIcon size={14} className="text-gray-300" />
                                  </div>
                                )}
                                <span className="text-xs truncate">{m.filename}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          className="w-full mt-3 bg-[#8c3add] hover:bg-[#702eb0] text-white"
                          onClick={() => setImagePickerOpen(false)}
                          disabled={selectedImageIds.length === 0}
                        >
                          Done{selectedImageIds.length ? ` (${selectedImageIds.length} selected)` : ""}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-gray-400 italic">Selected images will attach when you click Save Changes below.</p>
                </div>
              )}
              {simType === "email" && !editingId && (
                <p className="text-xs text-gray-500 italic">Save the template first to attach images from Media.</p>
              )}

              {/* Attachments — now selected from Media library instead of local upload */}
              {simType === "email" && editingId && (
                <div className="border border-dashed border-gray-200 rounded-lg p-4 space-y-3" data-testid="attachments-section">
                  <div className="flex items-center gap-2">
                    <Paperclip size={16} className="text-[#1fa7a1]" />
                    <Label className="font-medium">Decoy Attachments</Label>
                    <span className="text-xs text-gray-400">from Media · downloads are tracked</span>
                  </div>

                  {/* Currently attached files */}
                  {attachments.length > 0 && (
                    <div className="space-y-1.5">
                      {attachments.map((att) => (
                        <div key={att.id} className="flex items-center gap-2 p-2 border rounded-lg bg-white" data-testid={`att-row-${att.id}`}>
                          <Paperclip size={14} className="text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{att.filename}</p>
                            <p className="text-[10px] text-gray-400">
                              {att.content_type} · {(att.size / 1024).toFixed(1)}KB
                            </p>
                          </div>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button type="button" variant="ghost" size="sm" className="p-1 h-auto hover:bg-red-50"
                                onClick={() => toggleAttachmentSelection(att.id)}>
                                <X size={14} className="text-red-500" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Remove attachment</TooltipContent>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Pick attachments from Media library — list only opens on "Select" click */}
                  <div className="flex items-center gap-2">
                    <Popover open={attachmentPickerOpen} onOpenChange={setAttachmentPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          data-testid="select-attachments-btn"
                        >
                          <Paperclip size={14} />
                          Select Attachments{selectedAttachmentIds.length ? ` (${selectedAttachmentIds.length})` : ""}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <p className="text-xs font-medium text-gray-500 mb-2">Attachments from Media</p>
                        {mediaAttachmentsLoading ? (
                          <div className="flex justify-center py-6">
                            <CulsightPageLoader variant="inline" />
                          </div>

                        ) : mediaAttachments.length === 0 ? (
                          <div className="py-4 text-center text-sm text-gray-400">
                            No attachments in Media library yet — upload some in the Media page first.
                          </div>
                        ) : (
                          <div className="space-y-1.5 max-h-64 overflow-y-auto">
                            {mediaAttachments.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center gap-2 border rounded-lg p-2 cursor-pointer transition-colors ${selectedAttachmentIds.includes(m.id)
                                  ? "border-[#1fa7a1] bg-[#1fa7a1]/5"
                                  : "border-gray-200 bg-white hover:bg-gray-50"
                                  }`}
                              >
                                <Checkbox
                                  checked={selectedAttachmentIds.includes(m.id)}
                                  onCheckedChange={() => toggleAttachmentSelection(m.id)}
                                />
                                <Paperclip size={14} className="text-gray-400 flex-shrink-0" />
                                <span className="text-xs flex-1 truncate">{m.filename}</span>
                                {m.size && (
                                  <span className="text-[10px] text-gray-400 flex-shrink-0">
                                    {(m.size / 1024).toFixed(1)}KB
                                  </span>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          className="w-full mt-3 bg-[#1fa7a1] hover:bg-[#178a85] text-white"
                          onClick={() => setAttachmentPickerOpen(false)}
                          disabled={selectedAttachmentIds.length === 0}
                        >
                          Done{selectedAttachmentIds.length ? ` (${selectedAttachmentIds.length} selected)` : ""}
                        </Button>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <p className="text-xs text-gray-400 italic">Selected attachments will attach when you click Save Changes below.</p>
                </div>
              )}
              {simType === "email" && !editingId && (
                <p className="text-xs text-gray-500 italic">Save the template first to attach files from Media.</p>
              )}

              <DialogFooter className="pt-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="submit" className="bg-[#8c3add] hover:bg-[#702eb0]" data-testid="template-submit-btn">
                      {editingId ? "Save Changes" : "Create Template"}
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Template Dialog */}
        <Dialog open={!!viewTemplate} onOpenChange={() => setViewTemplate(null)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{viewTemplate?.name}</DialogTitle>
            </DialogHeader>
            {viewTemplate && (
              <div className="space-y-4 mt-2">
                <div className="flex gap-2 flex-wrap">
                  <Badge className={SIMULATION_COLORS[viewTemplate.simulation_type || "email"]}>
                    {(viewTemplate.simulation_type || "email").toUpperCase()}
                  </Badge>
                  <Badge className={getDifficultyColor(viewTemplate.difficulty)}>{viewTemplate.difficulty}</Badge>
                  <Badge variant="outline">{viewTemplate.category}</Badge>
                </div>
                {viewTemplate.subject && (
                  <div>
                    <Label className="text-gray-500">Subject</Label>
                    <p className="mt-1 font-medium">{viewTemplate.subject}</p>
                  </div>
                )}
                {viewTemplate.html_body ? (
                  <div>
                    <Label className="text-gray-500">HTML Preview</Label>
                    <div className="mt-2 border rounded-lg overflow-hidden bg-white">
                      <iframe
                        srcDoc={viewTemplate.html_body}
                        className="w-full h-80"
                        sandbox="allow-same-origin"
                        title="Email Preview"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-gray-500">Body</Label>
                    <div className="mt-1 p-4 bg-gray-50 rounded-lg whitespace-pre-wrap text-sm">
                      {viewTemplate.body}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={() => setViewTemplate(null)}>Close</Button>
                </TooltipTrigger>
              </Tooltip>
              {viewTemplate && viewTemplate.simulation_type !== "whatsapp" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="bg-[#8c3add] hover:bg-[#702eb0] gap-2"
                      onClick={() => { const t = viewTemplate; setViewTemplate(null); openEdit(t); }}
                    >
                      <Edit size={16} /> Edit
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deleteTemplateId}
          onOpenChange={(open) => {
            if (!open) setDeleteTemplateId(null);
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 size={18} className="text-red-500" />
                Delete Template
              </DialogTitle>
            </DialogHeader>

            <div className="py-3">
              {templateToDelete && templateToDelete.deleteable === false ? (
                <p className="text-sm text-red-600">
                  This email template can't be deleted — it's currently in use.
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    Are you sure you want to delete this template?
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    This action cannot be undone.
                  </p>
                </>
              )}
            </div>

            <DialogFooter>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteTemplateId(null)}
                  >
                    Cancel
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Keep template</TooltipContent>
              </Tooltip>

              {(!templateToDelete || templateToDelete.deleteable !== false) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      onClick={handleDelete}
                      className="bg-red-500 hover:bg-red-600 text-white"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Permanently delete</TooltipContent>
                </Tooltip>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default EmailTemplates;